import { useCallback, useState, useEffect, useMemo } from 'react'
import cx from 'classnames'

import request from '~/utils/request'
import { Language, CountryId } from '~/utils/language'
import { useLanguage } from '~/context/language'
import { uaIs } from '~/utils/ua'
import ActionSheet from '~/components/ActionSheet'
import Loading from '~/components/Loading'
import LanguageTab from '~/components/LanguageTab'
import { IOS_DOWNLOAD_LINK, ANDROID_DOWNLOAD_LINK } from '~/constants'

import BrowserHint from './components/BrowserHint'

import BgEnImage from './images/bg_en.png'
import BgHkImage from './images/bg_hk.png'
import BgJpImage from './images/bg_jp.png'
import LogoImage from './images/logo.png'
import styles from './DownloadPage.module.css'

type LanguageSetting = {
  language: Language
  name: string
  background: string
  infoHint: string
  buttonText: string
}

const LANGUAGE_SETTING_LIST: LanguageSetting[] = [
  {
    language: Language.EnUS,
    background: BgEnImage,
    name: 'EN',
    infoHint:
      'The World’s 1st Globally Connected\nSharing Power Bank Hong Kong • Japan',
    buttonText: 'Download APP',
  },
  {
    language: Language.ZhHK,
    background: BgHkImage,
    name: '中文',
    infoHint: '首個全球共享充電 香港 • 日本',
    buttonText: '下载 APP',
  },
  {
    language: Language.JaJP,
    background: BgJpImage,
    name: '日本語',
    infoHint:
      'どこでも借りられて、どこでも返せる\nスマホ充電シェアリングサービス',
    buttonText: '今すぐダウンロード',
  },
]

function DownloadPage() {
  const currentLanguage = useLanguage()
  const [language, setLanguage] = useState<Language>(currentLanguage)

  // Download
  const [showDownloadSheet, setShowDownloadSheet] = useState(false)
  const [showBrowserHint, setShowBrowserHint] = useState(false)
  const download = useCallback(() => {
    if (uaIs('Android') && uaIs('WeChat')) {
      setShowBrowserHint(true)
      return
    }
    window.location.href = uaIs('Android')
      ? ANDROID_DOWNLOAD_LINK
      : IOS_DOWNLOAD_LINK
  }, [])
  const onDownloadButtonClick = useCallback(() => {
    if (uaIs('Android') && language === Language.ZhHK) {
      setShowDownloadSheet(true)
      return
    }
    download()
  }, [language, download])

  // Auto Redirect
  const { type, uuid, dappReferrer } = useMemo(() => {
    const searchParams = new URLSearchParams(window.location.search)
    return {
      type: searchParams.get('type'),
      uuid: searchParams.get('uuid'),
      dappReferrer: searchParams.get('dapp_referrer'),
    }
  }, [])
  const isAutoRedirect = useMemo(() => {
    return (
      dappReferrer === 'dBarai' ||
      uaIs('UnionPay') ||
      uaIs('PayPay') ||
      uaIs('LINE')
    )
  }, [dappReferrer])
  useEffect(() => {
    // Dpay
    if (dappReferrer === 'dBarai') {
      const url = new URL('dpayment://miniapp')
      const customParam = JSON.stringify({ uuid, type: Number(type) })
      url.searchParams.set('miniapp_id', 'dmYogKxuPBCszSyB')
      url.searchParams.set('custom_param', customParam)
      window.location.href = url.toString()
      return
    }

    // UnionPay
    if (uaIs('UnionPay')) {
      request<CountryId>(`/v3/cabinet/${type}/${uuid}`, {
        data: { language },
      }).then(({ dataInfo: cabinetCountryId, status }) => {
        // will not jump if api failed
        if (status !== 10001) return

        let encryptAppId
        if (cabinetCountryId === CountryId.JP) {
          encryptAppId =
            window.CHARGESPOT_APP_ENV === 'prod'
              ? '7432d8a81870a15c'
              : 'c88c5f7130170342'
        }
        if (cabinetCountryId === CountryId.HK) {
          encryptAppId =
            window.CHARGESPOT_APP_ENV === 'prod'
              ? '13a8e41049ae6eaf'
              : '308ab13cab8086a4'
        }
        // failed with no country id
        if (!encryptAppId) return

        const url = new URL('upwallet://applet')
        url.searchParams.set('encryptAppId', encryptAppId)
        const industryReserve = [type, uuid].join(',')
        url.searchParams.set('industryReserve', industryReserve)
        window.location.href = url.toString()
        return
      })
      return
    }

    // PayPay
    if (uaIs('PayPay')) {
      const options = JSON.stringify({ uuid, type: Number(type) })
      const redirectUrl = new URL(
        window.CHARGESPOT_APP_ENV === 'prod'
          ? 'https://paypay.appchargespot.com'
          : 'https://stg-paypay.appchargespot.com'
      )
      redirectUrl.searchParams.set('options', options)
      const url = new URL('paypay://miniapp')
      const appId =
        window.CHARGESPOT_APP_ENV === 'prod' ? 'm_JffPo56GiT' : 'm_m0teMSbslJ'
      url.searchParams.set('appId', appId)
      url.searchParams.set('redirectUrl', redirectUrl.toString())
      url.searchParams.set('closeCurrentScreen', 'true')
      window.location.href = url.toString()
      return
    }

    // LINE
    // Android always trying to direct to line
    if (type && uuid && (uaIs('LINE') || uaIs('Android'))) {
      request<CountryId>(`/v3/cabinet/${type}/${uuid}`, {
        data: { language },
      }).then(({ dataInfo: cabinetCountryId, status }) => {
        // will not jump if api failed
        if (status !== 10001) return

        let scheme
        if (cabinetCountryId === CountryId.JP) {
          scheme =
            window.CHARGESPOT_APP_ENV === 'prod'
              ? 'line://app/1656114681-5EEK6xaK'
              : 'line://app/1656114679-ZEAPWRXD'
        }
        if (cabinetCountryId === CountryId.TW) {
          scheme =
            window.CHARGESPOT_APP_ENV === 'prod'
              ? 'https://miniapp.line.me/2008253149-0mw7dWZR'
              : 'https://miniapp.line.me/2008253147-Bd8QKmvM'
        }
        if (cabinetCountryId === CountryId.TH) {
          scheme =
            window.CHARGESPOT_APP_ENV === 'prod'
              ? 'line://app/1653539159-0yE6w7oJ'
              : // commented old non-prod scheme line://app/1653364462-dAqNvYZy
                'line://app/1653539159-04JoXqWA'
        }
        // failed with no country id
        if (!scheme) return

        const url = new URL(scheme)
        if (type) url.searchParams.set('type', type)
        if (uuid) url.searchParams.set('uuid', uuid)

        window.location.href = url.toString()
      })
      return
    }
  }, [language, type, uuid, dappReferrer])

  const languageSetting = LANGUAGE_SETTING_LIST.find(
    (s) => s.language === language
  )

  if (!languageSetting) return null
  const { background, infoHint, buttonText } = languageSetting

  if (isAutoRedirect) {
    return (
      <div className={styles.root}>
        {uaIs('PayPay') && (
          <div className={styles.paypayHint}>
            【このページを閉じてPayPayアプリのホーム画面へ戻ってください】
          </div>
        )}
        {!uaIs('PayPay') && <Loading />}
      </div>
    )
  }

  return (
    <div
      className={styles.root}
      style={{ backgroundImage: `url(${background})` }}
    >
      <LanguageTab
        className={styles.languageTab}
        languageSettingList={LANGUAGE_SETTING_LIST}
        currentLanguage={language}
        onChange={setLanguage}
      />

      <div className={styles.infoContainer}>
        <img className={styles.logo} src={LogoImage} alt="ChargeSPOT Logo" />
        <div className={styles.infoText}>
          <div className={styles.infoTitle}>ChargeSPOT</div>
          <div
            className={cx(styles.infoHint, {
              [styles.infoHintLarge]: language === Language.ZhHK,
            })}
          >
            {infoHint}
          </div>
        </div>
      </div>

      <button className={styles.downloadButton} onClick={onDownloadButtonClick}>
        {buttonText}
      </button>

      {showDownloadSheet && (
        <ActionSheet
          actionList={[
            {
              text: 'Google Play 下載',
              onClick: () => {
                download()
              },
            },
            {
              text: '大陸用戶 下載',
              onClick: () => {
                window.alert('下载后请同意安装此应用')
                download()
              },
            },
          ]}
          onCancel={() => {
            setShowDownloadSheet(false)
          }}
        />
      )}

      {showBrowserHint && (
        <BrowserHint
          onClose={() => {
            setShowBrowserHint(false)
          }}
        />
      )}
    </div>
  )
}

export default DownloadPage
